import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Query, QueryResult } from "@material-table/core";
import { NotificationManager } from "react-notifications";
import DataTable from "../../../components/shared/DataTable/DataTable";
import FilteringControl from "../../../components/shared/FilteringControl/FilteringControl";
import InfoBox from "../../../components/shared/InfoBox/InfoBox";
import SearchBarWithActions from "../../../components/shared/SearchBarWithActions/SearchBarWithActions";
import { Loading } from "../../../components/shared";
import serviceSales, { TedNotice, TedCategory, TedCpvCode } from "../../../services/service-sales";
import { useDebouncedState } from "../../../util/hooks";
import { getDaysLeft } from "../../../util";
import TedNoticeDetailPanel from "./TedNoticeDetailPanel";
import BtChecklistSelect from "../../bt-checklist-select/bt-checklist-select";
import AppPlaceChecklist from "../../app-place-checklist/app-place-checklist";
import { cpvSections, matchesCpvQuery } from "./cpvSections";
import {
  placeSummary, categoryCell, formatValue, SORT_KEY_BY_FIELD, cpvCodesForCategories,
  categoriesStillCovered, winnerSummary,
} from "./tedDisplay";
import styles from "./TedTable.module.scss";
import AppTenderWatchButton from "../../app-tender-watch/app-tender-watch-button";
import { SharedTenderFilter } from "../../app-tender-watch/useSharedTenderFilter";
import type { TedNoticeQuery, TedNoticeStatus } from "../../../services/service-sales";
import AppTedStarButton from "../../app-ted-star-button/app-ted-star-button";
import { TedStarsProvider, useTedStars } from "../../app-ted-star-button/useTedStar";

const PAGE_SIZE = 10;

// DataTable ignores defaultOrderByField unless given the column index too.
const DEFAULT_SORT_FIELD = "publicationDate";

type Copy = { key: string, fallback: string };

type TedView = {
  label: Copy,
  note: Copy | null,
  usesAttributeFilters: boolean,
  trailingColumns: "deadlineAndValue" | "winners",
};

// The pills follow this order.
const TED_VIEWS: Record<TedNoticeStatus, TedView> = {
  open: {
    label: { key: "published.ted.status.open", fallback: "Öppna" },
    note: null,
    usesAttributeFilters: true,
    trailingColumns: "deadlineAndValue",
  },
  awarded: {
    label: { key: "published.ted.status.awarded", fallback: "Tilldelade" },
    note: {
      key: "published.ted.introAwarded",
      fallback: "Resultat från tilldelningsmeddelanden i TED. De publiceras först när avtalet har tecknats, ofta flera veckor efter tilldelningsbeslutet, och för ungefär var tredje upphandling publiceras inget resultat alls.",
    },
    usesAttributeFilters: true,
    trailingColumns: "winners",
  },
  all: {
    label: { key: "published.ted.status.all", fallback: "Alla" },
    note: null,
    usesAttributeFilters: true,
    trailingColumns: "deadlineAndValue",
  },
  starred: {
    label: { key: "published.ted.status.starred", fallback: "Mina favoriter" },
    note: {
      key: "published.ted.introStarred",
      fallback: "Dina favoritmarkerade upphandlingar, oavsett filter. När TED publicerar resultatet för en favorit kommer det med veckomailet på onsdagar, om Offentliga upphandlingar är påslaget under Aviseringar i din profil.",
    },
    // The bevakning and the presentation pre-fill these, which would silently hide a star.
    usesAttributeFilters: false,
    trailingColumns: "deadlineAndValue",
  },
};

const formatDate = (date?: string | null) =>
  (date ? moment(date).locale("sv").format("D MMMM YYYY") : "");

// A header needs its own block to end in "…": th never clips, and a bare string can't shrink.
const columnTitle = (text: string) => (
  <span className={styles.columnTitle} title={text}>{text}</span>
);

type Props = {
  filter: SharedTenderFilter,
  /** Both tabs stay mounted once visited, so the one out of view must not keep querying. */
  hidden?: boolean,
};

/** Filters, sorts and pages on the server: the dataset grows without bound. */
export const TedTable: FC<Props> = ({ filter, hidden = false }) => {
  const [t] = useTranslation("requests");
  // material-table exposes no ref type; only onQueryChange is used.
  const tableRef = useRef<{ onQueryChange: (query?: { page?: number }) => void } | null>(null);

  const [search, setSearch, debouncedSearch] = useDebouncedState("", 300);
  const { areaIds, setAreaIds, places, setPlaces } = filter;
  const [categories, setCategories] = useState<TedCategory[]>([]);
  const [cpvOptions, setCpvOptions] = useState<TedCpvCode[]>([]);
  const [buyerNames, setBuyerNames] = useState<string[]>([]);
  const [buyers, setBuyers] = useState<string[]>([]);
  const [cpvCodes, setCpvCodes] = useState<string[]>([]);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [status, setStatus] = useState<TedNoticeStatus>("open");
  const view = TED_VIEWS[status];

  // Settled rather than all: one selector failing to load should not blank the other.
  useEffect(() => {
    (async () => {
      const [categoryResult, cpvResult, buyerResult] = await Promise.allSettled([
        serviceSales.fetchTedCategories(),
        serviceSales.fetchTedCpvCodes(),
        serviceSales.fetchTedBuyers(),
      ]);

      if (categoryResult.status === "fulfilled") setCategories(categoryResult.value);
      else {
        NotificationManager.error(
          String(categoryResult.reason),
          t("published.ted.categoriesError", "Kunde inte hämta kategorier"),
        );
      }

      if (cpvResult.status === "fulfilled") setCpvOptions(cpvResult.value);
      else {
        NotificationManager.error(
          String(cpvResult.reason),
          t("published.ted.cpvCodesError", "Kunde inte hämta CPV-koder"),
        );
      }

      if (buyerResult.status === "fulfilled") setBuyerNames(buyerResult.value);
      else {
        NotificationManager.error(
          String(buyerResult.reason),
          t("published.ted.buyersError", "Kunde inte hämta beställare"),
        );
      }

      setLoadingFilters(false);
    })();
  }, [t]);

  /**
   * What a save and the list query both stand on, derived once so they cannot disagree.
   *
   * A category is stored as a category, so the bevakning follows later corrections to the CPV
   * mapping instead of freezing today's expansion. Pruning a code out of a category's expansion
   * drops the category in the CPV handler, where the user's intent is known - not here, where
   * an empty control cannot be told apart from a deliberately emptied one.
   *
   * Until the CPV vocabulary has loaded there is nothing to say about codes, so the categories
   * stand alone and no code is sent either way.
   */
  const cpvVocabularyReady = cpvOptions.length > 0;
  const watchFilter = useMemo(() => {
    if (!cpvVocabularyReady) return { areaIds, cpvCodes: undefined as string[] | undefined };
    const fromCategories = new Set(cpvCodesForCategories(areaIds, categories));
    return { areaIds, cpvCodes: cpvCodes.filter((code) => !fromCategories.has(code)) };
  }, [areaIds, categories, cpvCodes, cpvVocabularyReady]);

  const categoryOptions = useMemo(
    () => categories.map((category) => ({ value: category.id, label: category.name })),
    [categories],
  );

  // A category with no CPV code in the TED data is not offered here at all.
  const offeredCategories = useMemo(() => new Set(categories.map((category) => category.id)), [categories]);

  const selectedCategories = useMemo(
    () => areaIds.filter((id) => offeredCategories.has(id)),
    [areaIds, offeredCategories],
  );

  const cpvChecklist = useMemo(() => cpvSections(cpvOptions), [cpvOptions]);

  const buyerOptions = useMemo(
    () => buyerNames.map((name) => ({ value: name, label: name })),
    [buyerNames],
  );

  /**
   * Picking a yrkeskategori fills its CPV codes into the CPV control, so the mapping is
   * visible and adjustable rather than applied invisibly on the server. Only the codes go
   * to the API; the category is the shortcut that selected them.
   */
  const onCategoryChange = useCallback((chosen: string[]) => {
    // One chosen on the private tab but not offered here is missing from `chosen`, so keep it.
    setAreaIds([...areaIds.filter((id) => !offeredCategories.has(id)), ...chosen]);
  }, [areaIds, offeredCategories, setAreaIds]);

  /**
   * Removing a code the user never picked by hand means the category that brought it in no
   * longer describes what they want. Dropping the category here, rather than inferring it
   * later from the state of the control, keeps the shared filter the single account of it -
   * otherwise the other tab still shows the category and one click there brings the code back.
   */
  const previousAreaIds = useRef<string[]>([]);
  const onCpvChange = useCallback((next: string[]) => {
    const kept = categoriesStillCovered(areaIds, categories, next);
    if (kept.length !== areaIds.length) {
      // The seeding effect withdraws the codes a departing category brought in. A category
      // dropped here departs because one of its codes was removed and the rest are still
      // wanted, so the transition is recorded as already reconciled.
      previousAreaIds.current = kept;
      setAreaIds(kept);
    }
    setCpvCodes(next);
  }, [areaIds, categories, setAreaIds]);

  /**
   * The CPV control follows the chosen categories, so the mapping is visible and adjustable
   * rather than applied invisibly on the server. Categories are shared with the other tab, so
   * this runs for one picked there too - which is why it takes the codes the departing
   * categories brought in and adds what the arriving ones bring, instead of replacing the
   * selection wholesale and discarding whatever the user picked by hand.
   */
  const seededCpv = useRef(false);
  useEffect(() => {
    if (!filter.ready || !cpvOptions.length) return;
    const offered = new Set(cpvOptions.map((entry) => entry.code));

    const leaving = new Set(cpvCodesForCategories(previousAreaIds.current, categories));
    const arriving = cpvCodesForCategories(areaIds, categories);
    previousAreaIds.current = areaIds;

    setCpvCodes((current) => {
      const wanted = new Set(current.filter((code) => !leaving.has(code)));
      arriving.forEach((code) => wanted.add(code));
      if (!seededCpv.current) {
        seededCpv.current = true;
        filter.initialCpvCodes.forEach((code) => wanted.add(code));
      }
      return Array.from(wanted).filter((code) => offered.has(code));
    });
  }, [areaIds, categories, cpvOptions, filter.ready, filter.initialCpvCodes]);

  // A view's answer can land after the reader has moved on to another view.
  const shownView = useRef(status);
  shownView.current = status;

  // A favourite put on or taken off has to show in Mina favoriter, and in the count with it.
  const reloadFavourites = useCallback(() => {
    if (shownView.current === "starred") tableRef.current?.onQueryChange();
  }, []);

  // Held here, above the table that remounts per view, so a star still saving survives a switch.
  const stars = useTedStars(reloadFavourites);
  const { settledSoFar, releaseSettled } = stars;

  const queryFilters: Pick<TedNoticeQuery, "areaIds" | "cpvCodes" | "buyers" | "places"> = useMemo(
    () => (view.usesAttributeFilters ? {
      areaIds: watchFilter.areaIds.length ? watchFilter.areaIds : undefined,
      cpvCodes: watchFilter.cpvCodes?.length ? watchFilter.cpvCodes : undefined,
      buyers: buyers.length ? buyers : undefined,
      places: places.length ? places : undefined,
    } : {}),
    [view, watchFilter, buyers, places],
  );

  const fetchPage = useCallback((query: Query<TedNotice>): Promise<QueryResult<TedNotice>> => {
    const order = query.orderByCollection?.[0];
    const sortKey = order?.orderByField ? SORT_KEY_BY_FIELD[order.orderByField] : undefined;
    const sort = sortKey
      ? `${order.orderDirection === "desc" ? "-" : ""}${sortKey}`
      : undefined;

    const settledBeforeRead = settledSoFar();
    return serviceSales
      .fetchTedNotices({
        page: query.page,
        pageSize: query.pageSize,
        status,
        search: debouncedSearch || undefined,
        ...queryFilters,
        sort,
      })
      .then((result) => {
        releaseSettled(settledBeforeRead);
        if (shownView.current === status) setTotalCount(result.totalCount);
        return { data: result.data, page: result.page, totalCount: result.totalCount };
      })
      .catch((e) => {
        if (shownView.current === status) {
          NotificationManager.error(
            (e as Error).toString(),
            t("published.ted.error", "Kunde inte hämta upphandlingar från TED"),
          );
          setTotalCount(0);
        }
        // An empty page keeps the app's own empty state instead of material-table's
        // untranslated error row.
        return { data: [], page: 0, totalCount: 0 };
      });
  }, [status, debouncedSearch, queryFilters, t, settledSoFar, releaseSettled]);

  // A view brings its own columns and sorts; a fresh table asks once for its first page.
  const tableKey = status;

  // In remote mode material-table ignores a changed `data` function, so paging and sorting
  // refetch by themselves but a filter change needs poking.
  //
  // Compared by content, not identity: choosing a category commits twice, the vocabularies
  // arriving give `watchFilter` a new identity describing the same question, and switching
  // tabs is navigation rather than a filter change. What the table already shows is recorded
  // only when a query is actually issued, so an edit made on the other tab while this one is
  // hidden stays owed until it returns, and is then spent once.
  const onScreen = useRef<{ table: string, asked: string } | null>(null);
  useEffect(() => {
    // Sorted: the filters keep picks in the order they were made, which does not change the question.
    const asked = JSON.stringify([
      debouncedSearch,
      Object.entries(queryFilters).map(([name, picks]) => [name, picks && [...picks].sort()]),
    ]);
    if (onScreen.current === null || onScreen.current.table !== tableKey) {
      onScreen.current = { table: tableKey, asked };
      return;
    }
    if (hidden || asked === onScreen.current.asked) return;
    onScreen.current = { table: tableKey, asked };
    tableRef.current?.onQueryChange({ page: 0 });
  }, [tableKey, debouncedSearch, queryFilters, hidden]);

  // Explicit widths with a fixed layout: auto layout re-measures from content, so sorting
  // one column shifts the others.
  const columns = useMemo(() => [
    {
      title: columnTitle(t("table.header.tedTitle", "Upphandling")),
      field: "title",
      width: "25%",
      render: (row: TedNotice) => (
        <div className={styles.titleCell}>
          {row.procedureIdentifier && (
            <AppTedStarButton
              procedureIdentifier={row.procedureIdentifier}
              starred={Boolean(row.isStarred)}
            />
          )}
          <div className={styles.titleText}>
            <div className={styles.title} title={row.title}>{row.title}</div>
            <div className={styles.publicationNumber}>{row.publicationNumber}</div>
          </div>
        </div>
      ),
    },
    {
      title: columnTitle(t("table.header.org", "Beställare")),
      field: "buyer.name",
      width: "13%",
      sorting: false,
      render: (row: TedNotice) => (
        <span title={row.buyer?.name ?? undefined}>{row.buyer?.name}</span>
      ),
    },
    {
      title: columnTitle(t("table.header.category", "Yrkeskategori")),
      field: "category.name",
      width: "14%",
      sorting: false,
      render: (row: TedNotice) => {
        const cell = categoryCell(row.category, row.cpv);
        if (cell.kind === "none") return cell.text;
        if (cell.kind === "category") return <span title={cell.text}>{cell.text}</span>;
        return (
          <span
            className={styles.cpvFallback}
            title={t(
              "published.ted.cpvFallbackHint",
              "TED:s egen kategori (CPV). Inte kopplad till en yrkeskategori i Accurator.",
            )}
          >
            {cell.text}
          </span>
        );
      },
    },
    {
      title: columnTitle(t("table.header.region", "Region")),
      field: "places",
      width: "12%",
      sorting: false,
      render: (row: TedNotice) => {
        const { text, title } = placeSummary(row.places);
        return <span title={title}>{text}</span>;
      },
    },
    {
      title: columnTitle(t("table.header.publishedAt", "Publicerad")),
      field: "publicationDate",
      width: "13%",
      render: (row: TedNotice) => formatDate(row.publicationDate),
    },
    ...(view.trailingColumns === "winners" ? [{
      title: columnTitle(t("table.header.winners", "Vinnare")),
      // No width: as the last column the stylesheet gives it whatever the others leave.
      field: "award",
      sorting: false,
      render: (row: TedNotice) => {
        if (!row.award) return "–";
        const summary = row.award.awarded ? winnerSummary(row.award.winners) : null;
        if (summary) return <span title={summary.title}>{summary.text}</span>;
        return row.award.awarded
          ? t("published.ted.winner.unnamed", "Vinnare ej angiven")
          : t("published.ted.winner.notAwarded", "Ingen tilldelning");
      },
    }] : [{
      title: columnTitle(t("table.header.timeLeft", "Anbudstid")),
      field: "deadline",
      width: "11%",
      render: (row: TedNotice) => {
        if (row.award) {
          return row.award.awarded
            ? t("table.row.awarded", "Tilldelad")
            : t("published.ted.winner.notAwarded", "Ingen tilldelning");
        }
        if (!row.deadline) return "–";
        const daysLeft = getDaysLeft(row.deadline);
        return isNaN(+daysLeft)
          ? daysLeft
          : t("table.row.timeLeft", "{{count}} dagar", {
            context: +daysLeft < 0 ? "expired" : null,
            count: +daysLeft,
          });
      },
    },
    {
      title: columnTitle(t("table.header.tedValue", "Värde")),
      field: "estimatedValue",
      width: "12%",
      render: (row: TedNotice) => {
        const value = formatValue(row.estimatedValue, row.estimatedValueCurrency);
        return <span title={value}>{value}</span>;
      },
    }]),
  ], [t, view.trailingColumns]);

  const renderDetailPanel = useCallback(
    ({ rowData }: { rowData: TedNotice }) => <TedNoticeDetailPanel notice={rowData} />,
    [],
  );

  if (loadingFilters) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
        <Loading type="inline" />
      </div>
    );
  }

  const statusOptions = (Object.keys(TED_VIEWS) as TedNoticeStatus[]).map((value) => ({
    value,
    label: t(TED_VIEWS[value].label.key, TED_VIEWS[value].label.fallback),
    onSelection: () => setStatus(value),
  }));

  return (
    <div>
      <InfoBox
        customStyle={{ marginBottom: "24px" }}
        description={t(
          "published.ted.intro",
          "Offentliga upphandlingar i Sverige, publicerade i EU:s annonsdatabas TED. Anbud lämnas hos beställaren, inte i Accurator.",
        )}
      />

      <SearchBarWithActions
        className={styles.filters}
        value={search}
        onChange={setSearch}
        placeholder={t("published.ted.searchPlaceholder", "Sök på titel, beställare, vinnare, orgnr eller annonsnummer")}
      >
        <div
          className={`${styles.filterRow} ${view.usesAttributeFilters ? "" : styles.filtersOutOfUse}`}
          // React 18 has no inert prop; the attribute keeps the row in view but out of reach.
          ref={(row) => row?.toggleAttribute("inert", !view.usesAttributeFilters)}
        >
          <BtChecklistSelect
            placeholder={t("published.ted.categorySelector", "Yrkeskategori")}
            options={categoryOptions}
            value={selectedCategories}
            onChange={onCategoryChange}
          />
          <BtChecklistSelect
            placeholder={t("published.ted.cpvSelector", "CPV-kod")}
            searchPlaceholder={t("published.ted.cpvSearch", "Sök kod eller benämning…")}
            options={cpvChecklist}
            value={cpvCodes}
            onChange={onCpvChange}
            matchesQuery={matchesCpvQuery}
          />
          <BtChecklistSelect
            placeholder={t("published.ted.buyerSelector", "Beställare")}
            options={buyerOptions}
            value={buyers}
            onChange={setBuyers}
          />
          <AppPlaceChecklist places={places} onChange={setPlaces} />
          {filter.enabled && (
            <div className={styles.watchAction}>
              <AppTenderWatchButton
                areaIds={watchFilter.areaIds}
                places={places}
                cpvCodes={watchFilter.cpvCodes}
                saved={filter.saved}
                onSaved={filter.markSaved}
              />
            </div>
          )}
        </div>
      </SearchBarWithActions>

      <div className={styles.statusRow}>
        <FilteringControl options={statusOptions} value={status} />
        <span className={styles.resultCount}>
          {t("published.ted.resultCount", {
            count: totalCount,
            defaultValue_one: "{{count}} upphandling",
            defaultValue_other: "{{count}} upphandlingar",
          })}
        </span>
      </div>
      {view.note && <p className={styles.viewNote}>{t(view.note.key, view.note.fallback)}</p>}

      <div className={styles.table}>
        <TedStarsProvider value={stars}>
          <DataTable<TedNotice>
            key={tableKey}
            tableRef={tableRef}
            columns={columns}
            data={fetchPage}
            isLoading={false}
            pageSize={PAGE_SIZE}
            tableLayout="fixed"
            // material-table types the third argument as the toggle or a RowAction, and only
            // one of those is callable.
            onRowClick={(_event, _rowData, togglePanel) => {
              if (typeof togglePanel === "function") togglePanel();
            }}
            // DataTable's empty-state guard only inspects an array `data`, so a remote table
            // must withhold the panel itself or the empty row grows a phantom column.
            detailPanel={totalCount > 0 ? renderDetailPanel : undefined}
            defaultOrderBy={columns.findIndex((column) => column.field === DEFAULT_SORT_FIELD)}
            defaultOrderByField={DEFAULT_SORT_FIELD}
            defaultOrderDirection="desc"
          />
        </TedStarsProvider>
      </div>

      <p className={styles.attribution}>
        {t(
          "published.ted.attribution",
          "Källa: TED (Tenders Electronic Daily), Europeiska unionens publikationsbyrå. Vidareutnyttjande enligt beslut 2011/833/EU.",
        )}{" "}
        <a href="https://ted.europa.eu" target="_blank" rel="noopener noreferrer">
          {t("published.ted.attributionLink", "Om TED")}
        </a>
      </p>
    </div>
  );
};

export default TedTable;
